// src/components/admin/RoleTemplateModal.tsx
// REDESIGNED: Two-step flow for easier template creation

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { RoleTemplate } from '../../types/onboarding';
import { categoryAPI } from '../../services/categoryAPI';

interface RoleTemplateModalProps {
  isOpen: boolean;
  template: RoleTemplate | null;
  isCreateMode: boolean;
  onClose: () => void;
  onSave: (templateData: Partial<RoleTemplate>) => Promise<void>;
}

// Simplified category structure for templates
interface TemplateCategory {
  categoryId: string;
  categoryName: string;
  quantityRequired: number;
  requiresAttestation: boolean;
}

interface CategoryOption {
  categoryId: string;
  displayName: string;
  isFirearm: boolean; // For auto-attestation
}

type Step = 'categories' | 'configure';

const RoleTemplateModal: React.FC<RoleTemplateModalProps> = ({
  isOpen,
  template,
  isCreateMode,
  onClose,
  onSave
}) => {
  // Form state
  const [roleName, setRoleName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(60);
  const [autoExpireHours, setAutoExpireHours] = useState(72);
  const [isActive, setIsActive] = useState(true);
  
  // Category state
  const [availableCategories, setAvailableCategories] = useState<CategoryOption[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [categoryConfigs, setCategoryConfigs] = useState<Map<string, TemplateCategory>>(new Map());
  
  // UI state
  const [currentStep, setCurrentStep] = useState<Step>('categories');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load categories and initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableCategories();
      initializeForm();
      setCurrentStep('categories');
    }
  }, [isOpen, template]);

  const loadAvailableCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      
      if (response.success && response.data?.categories) {
        const categories = response.data.categories;
        const options: CategoryOption[] = [];
        
        const parentCategories = categories.filter((cat: any) => !cat.parentCategoryId);
        
        parentCategories.forEach((parent: any) => {
          const children = categories.filter((cat: any) => cat.parentCategoryId === parent.categoryId);
          
          if (children.length > 0) {
            children.forEach((child: any) => {
              const name = `${parent.name} → ${child.name}`;
              options.push({
                categoryId: child.categoryId,
                displayName: name,
                isFirearm: name.toLowerCase().includes('firearm') || 
                           parent.name.toLowerCase().includes('firearm')
              });
            });
          } else {
            options.push({
              categoryId: parent.categoryId,
              displayName: parent.name,
              isFirearm: parent.name.toLowerCase().includes('firearm')
            });
          }
        });
        
        setAvailableCategories(options);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      setAvailableCategories([]);
    }
  };

  const initializeForm = () => {
    if (template && !isCreateMode) {
      setRoleName(template.roleName);
      setDisplayName(template.displayName);
      setDescription(template.description);
      setEstimatedDuration(template.estimatedDuration);
      setAutoExpireHours(template.autoExpireHours);
      setIsActive(template.isActive);
      
      // Initialize selected categories from existing template
      const selectedIds = new Set<string>();
      const configs = new Map<string, TemplateCategory>();
      
      template.categories?.forEach(cat => {
        selectedIds.add(cat.categoryId);
        configs.set(cat.categoryId, {
          categoryId: cat.categoryId,
          categoryName: cat.categoryName,
          quantityRequired: cat.quantityRequired || 1,
          requiresAttestation: cat.requiresAttestation || false
        });
      });
      
      setSelectedCategoryIds(selectedIds);
      setCategoryConfigs(configs);
    } else {
      // Reset for new template
      setRoleName('');
      setDisplayName('');
      setDescription('');
      setEstimatedDuration(60);
      setAutoExpireHours(72);
      setIsActive(true);
      setSelectedCategoryIds(new Set());
      setCategoryConfigs(new Map());
    }
    
    setValidationErrors({});
    setError(null);
  };

  const handleCategoryToggle = (category: CategoryOption) => {
    const newSelected = new Set(selectedCategoryIds);
    const newConfigs = new Map(categoryConfigs);
    
    if (newSelected.has(category.categoryId)) {
      newSelected.delete(category.categoryId);
      newConfigs.delete(category.categoryId);
    } else {
      newSelected.add(category.categoryId);
      // Initialize with defaults, auto-check attestation for firearms
      newConfigs.set(category.categoryId, {
        categoryId: category.categoryId,
        categoryName: category.displayName,
        quantityRequired: 1,
        requiresAttestation: category.isFirearm
      });
    }
    
    setSelectedCategoryIds(newSelected);
    setCategoryConfigs(newConfigs);
  };

  const handleQuantityChange = (categoryId: string, quantity: number) => {
    const config = categoryConfigs.get(categoryId);
    if (config) {
      setCategoryConfigs(new Map(categoryConfigs.set(categoryId, {
        ...config,
        quantityRequired: Math.max(1, quantity)
      })));
    }
  };

  const handleAttestationToggle = (categoryId: string) => {
    const config = categoryConfigs.get(categoryId);
    if (config) {
      setCategoryConfigs(new Map(categoryConfigs.set(categoryId, {
        ...config,
        requiresAttestation: !config.requiresAttestation
      })));
    }
  };

  const validateStep1 = (): boolean => {
    if (selectedCategoryIds.size === 0) {
      setError('Select at least one category');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!roleName.trim()) errors.roleName = 'Role name is required';
    if (!displayName.trim()) errors.displayName = 'Display name is required';
    if (estimatedDuration < 1) errors.estimatedDuration = 'Must be at least 1 minute';
    if (autoExpireHours < 1) errors.autoExpireHours = 'Must be at least 1 hour';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep('configure');
    }
  };

  const handleBack = () => {
    setCurrentStep('categories');
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const categories = Array.from(categoryConfigs.values());
      
      await onSave({
        roleName: roleName.trim(),
        displayName: displayName.trim(),
        description: description.trim(),
        estimatedDuration,
        autoExpireHours,
        isActive,
        categories
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isCreateMode ? 'Create Role Template' : 'Edit Role Template'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 'categories' ? 'Step 1: Select equipment categories' : 'Step 2: Configure template details'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Step 1: Category Selection */}
          {currentStep === 'categories' && (
            <div>
              <p className="text-gray-600 mb-4">
                Select all equipment categories this role requires. You'll configure quantities in the next step.
              </p>
              
              {availableCategories.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-700">
                    No categories available. Configure categories in tenant settings first.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableCategories.map(category => (
                    <label
                      key={category.categoryId}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedCategoryIds.has(category.categoryId)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategoryIds.has(category.categoryId)}
                        onChange={() => handleCategoryToggle(category)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-3 text-gray-900">{category.displayName}</span>
                      {category.isFirearm && (
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Attestation Required
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              
              {selectedCategoryIds.size > 0 && (
                <p className="mt-4 text-sm text-gray-600">
                  {selectedCategoryIds.size} categor{selectedCategoryIds.size === 1 ? 'y' : 'ies'} selected
                </p>
              )}
            </div>
          )}

          {/* Step 2: Configure Template */}
          {currentStep === 'configure' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={roleName}
                    onChange={e => setRoleName(e.target.value)}
                    placeholder="e.g., Patrol Officer"
                    className={`w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500 ${
                      validationErrors.roleName ? 'border-red-300' : ''
                    }`}
                  />
                  {validationErrors.roleName && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.roleName}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="e.g., Patrol Officer (Standard)"
                    className={`w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500 ${
                      validationErrors.displayName ? 'border-red-300' : ''
                    }`}
                  />
                  {validationErrors.displayName && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.displayName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Describe what this role template is used for..."
                  className="w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              {/* Settings Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (min)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={estimatedDuration}
                    onChange={e => setEstimatedDuration(parseInt(e.target.value) || 1)}
                    className="w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auto-expire (hrs)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={autoExpireHours}
                    onChange={e => setAutoExpireHours(parseInt(e.target.value) || 1)}
                    className="w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                
                <div className="flex items-end pb-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={e => setIsActive(e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Category Configuration Table */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Equipment Categories ({selectedCategoryIds.size})
                </h3>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Category
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">
                          Attestation
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.from(categoryConfigs.values()).map(config => (
                        <tr key={config.categoryId}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {config.categoryName}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="number"
                              min="1"
                              value={config.quantityRequired}
                              onChange={e => handleQuantityChange(config.categoryId, parseInt(e.target.value) || 1)}
                              className="w-16 text-center rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={config.requiresAttestation}
                              onChange={() => handleAttestationToggle(config.categoryId)}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Attestation is required for firearm categories to verify training certification.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          {currentStep === 'categories' ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={selectedCategoryIds.size === 0}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    {isCreateMode ? 'Create Template' : 'Save Changes'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleTemplateModal;